export const RIDE_TYPES = ['app', 'auto'];

export const C2C_ORDER_TYPES = ['c2c', 'auto_c2c', 'scooty_c2c', 'delivery'];

export const COLLECT_CASH_FROM = {
  RECEIVER: 'receiver',
  SENDER: 'sender',
};

export interface PaymentInfo {
  mode?: string;
  collectCashFrom?: string;
}

export interface C2cInfo {
  collectCashFrom?: string;
}

function normalizeCollectCashFrom(value?: string): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function isParcelOrderType(orderType?: string): boolean {
  return !!orderType && C2C_ORDER_TYPES.indexOf(orderType) !== -1;
}

export function getPaymentModeDisplayText(mode?: string): string {
  if (!mode) {
    return '';
  }

  const trimmedMode = mode.trim();
  return trimmedMode ? `Via ${trimmedMode}` : '';
}

export function isDropPhase(status?: string, orderType?: string): boolean {
  return status === 'started';
}

export function isPickupPhaseStatus(status?: string): boolean {
  return status === 'onTheWay' || status === 'arrived';
}

export function getPinLabel(orderType?: string): string {
  if (orderType === 'delivery') {
    return 'Delivery OTP';
  }
  if (isParcelOrderType(orderType)) {
    return 'PIN for Parcel Pickup';
  }
  return 'PIN to start ride';
}

export function getStartedEtaText(orderType?: string): string {
  return isParcelOrderType(orderType)
    ? 'Reaching drop location in '
    : 'Reaching destination in ';
}

function normalizeOtpValue(value: any): string | null {
  if (value == null) {
    return null;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}

export function getOtpDisplayString(data?: {
  otp?: any;
  orderType?: string;
  status?: string;
  packageDelivery?: { otp?: any; pickupOtp?: any };
  c2c?: { otp?: any; pickupOtp?: any };
  pickupOtp?: any;
  pickupOTP?: any;
}): string | null {
  if (!data || !isPickupPhaseStatus(data.status)) {
    return null;
  }

  const otp = data.otp;

  if (otp != null && typeof otp !== 'object') {
    return normalizeOtpValue(otp);
  }

  if (otp && typeof otp === 'object') {
    const nestedOtp = otp as {
      pickup?: { otp?: any };
      drop?: { otp?: any };
      value?: any;
    };

    if (nestedOtp.pickup && nestedOtp.pickup.otp != null) {
      const pickupOtp = normalizeOtpValue(nestedOtp.pickup.otp);
      if (pickupOtp) {
        return pickupOtp;
      }
    }

    if (nestedOtp.value != null) {
      const valueOtp = normalizeOtpValue(nestedOtp.value);
      if (valueOtp) {
        return valueOtp;
      }
    }

    if (nestedOtp.pickup && nestedOtp.pickup.otp != null) {
      return normalizeOtpValue(nestedOtp.pickup.otp);
    }
  }

  if (data.pickupOtp != null || data.pickupOTP != null) {
    const pickupOtp = normalizeOtpValue(
      data.pickupOtp != null ? data.pickupOtp : data.pickupOTP
    );
    if (pickupOtp) {
      return pickupOtp;
    }
  }

  if (data.packageDelivery) {
    if (data.packageDelivery.otp != null) {
      const packageOtp = normalizeOtpValue(data.packageDelivery.otp);
      if (packageOtp) {
        return packageOtp;
      }
    }

    if (data.packageDelivery.pickupOtp != null) {
      const packagePickupOtp = normalizeOtpValue(data.packageDelivery.pickupOtp);
      if (packagePickupOtp) {
        return packagePickupOtp;
      }
    }
  }

  if (data.c2c) {
    if (data.c2c.otp != null) {
      const c2cOtp = normalizeOtpValue(data.c2c.otp);
      if (c2cOtp) {
        return c2cOtp;
      }
    }

    if (data.c2c.pickupOtp != null) {
      const c2cPickupOtp = normalizeOtpValue(data.c2c.pickupOtp);
      if (c2cPickupOtp) {
        return c2cPickupOtp;
      }
    }
  }

  return null;
}

export function getPaymentDisplayText(
  payment?: PaymentInfo,
  c2c?: C2cInfo,
  orderType?: string
): string {
  const rawMode = payment && payment.mode;
  const mode = typeof rawMode === 'string' ? rawMode.trim() : '';
  if (!mode) {
    return '';
  }

  if (!isParcelOrderType(orderType)) {
    return `Paying via ${mode}`;
  }

  const collectCashFrom =
    normalizeCollectCashFrom(c2c && c2c.collectCashFrom) ||
    normalizeCollectCashFrom(payment.collectCashFrom);

  return collectCashFrom === COLLECT_CASH_FROM.RECEIVER
    ? 'Pay at drop'
    : 'Pay at pickup';
}
